import { Box, Button, Flex, Text } from '@opendoor/bricks/core';

import React, { useState } from 'react';
import {
  PromptGroup,
  getFirstPendingQuestionInCategory,
} from 'sellDirect/helpers/closingDash/disclosures';

import { Checkbox } from '@opendoor/bricks/core/Input/CheckboxOrRadio';
import { Question } from 'sellDirect/components/closingDash/disclosures/DynamicDisclosures/Question';

export interface CategoryFormProps {
  /** Callback to call when the customer submits the current step. */
  onSubmitStep: () => void;
  /** Determines if this is the submit step. */
  isSubmitStep: boolean;
  questionCategory: PromptGroup;
  /** Callback to save current progress. */
  saveProgress: () => void;
}

/**
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const CategoryForm: React.FC<CategoryFormProps> = ({
  onSubmitStep,
  questionCategory,
  isSubmitStep,
  saveProgress,
}) => {
  const buttonText = isSubmitStep ? 'Submit' : 'Next';
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const validateAndSaveResponse = () => {
    saveProgress();
    const pendingQuestion = getFirstPendingQuestionInCategory(questionCategory);

    // If there are pending questions OR the last step has not been acknowledged, show errors
    if (pendingQuestion || (isSubmitStep && !isAcknowledged)) {
      if (pendingQuestion) {
        const photoElement = document.getElementById(`question-${pendingQuestion?.id}`);
        photoElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setShowErrors(true);
    } else {
      setShowErrors(false);
      onSubmitStep();
    }
  };

  const userAcknowledgementComponent = (
    <Box>
      <Checkbox
        key="disclosure-confirmation-check"
        label={
          <Text fontSize="s1" fontWeight="semibold" width="100%">
            All information submitted in these disclosures is true and correct to the best of my
            knowledge
          </Text>
        }
        value="disclosure-confirmation-check"
        checked={isAcknowledged}
        analyticsName="disclosure-confirmation-check"
        onClick={() => setIsAcknowledged(!isAcknowledged)}
      />
      {showErrors && !isAcknowledged && (
        <Text color="red50" marginTop="3">
          Required
        </Text>
      )}
    </Box>
  );

  return (
    <Flex flexDirection="column" minWidth="327" maxWidth="400" marginTop="40" marginX="24" gap="32">
      <Text fontSize="s3" fontWeight="semibold">
        {questionCategory.title}
      </Text>
      <Flex flexDirection="column" gap="32">
        {questionCategory.prompts.map((question) => (
          <Question
            key={question.id}
            question={question}
            promptGroup={questionCategory}
            showErrors={showErrors}
          />
        ))}
        {isSubmitStep && userAcknowledgementComponent}
        <Button
          name={buttonText}
          onClick={validateAndSaveResponse}
          aria-label={buttonText.toLowerCase()}
          analyticsName={`${questionCategory.title}-form`}
          marginTop={16}
          marginBottom={32}
        >
          {buttonText}
        </Button>
      </Flex>
    </Flex>
  );
};
